import React from 'react';
import { motion } from 'motion/react';
import { Home, Sparkles, UsersRound } from 'lucide-react';
import type { WeddingPersonDetails } from '@/src/types';
import { Reveal } from './shared';

type Props = {
  groom: WeddingPersonDetails;
  bride: WeddingPersonDetails;
  story: string;
};

function hasPersonDetails(person: WeddingPersonDetails) {
  return Boolean(
    person.fullName?.trim() ||
    person.photoUrl?.trim() ||
    person.about?.trim() ||
    person.profession?.trim() ||
    person.fatherName?.trim() ||
    person.motherName?.trim() ||
    person.homeAddress?.trim(),
  );
}

function PersonStory({
  person,
  role,
  align = 'left',
}: {
  person: WeddingPersonDetails;
  role: 'Groom' | 'Bride';
  align?: 'left' | 'right';
}) {
  const name = person.fullName?.trim();
  if (!hasPersonDetails(person)) return null;
  const parents = [person.fatherName, person.motherName].filter(Boolean).join(' and ');

  return (
    <section
      className={`relative isolate min-h-[92svh] overflow-hidden px-5 py-20 text-[#fffaf0] sm:px-8 lg:px-14 lg:py-28 ${
        align === 'right' ? 'bg-[#19110f]' : 'bg-[#11130f]'
      }`}
    >
      {person.photoUrl ? (
        <motion.div
          aria-hidden="true"
          initial={{ scale: 1.08 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0 -z-30"
        >
          <img src={person.photoUrl} alt="" className="h-full w-full object-cover" />
        </motion.div>
      ) : (
        <div className="absolute inset-0 -z-30 bg-[linear-gradient(135deg,#3b211c,#11130f_55%,#1e221b)]" />
      )}
      <div
        className={`absolute inset-0 -z-20 ${
          align === 'right'
            ? 'bg-[linear-gradient(270deg,rgba(9,9,7,0.92),rgba(9,9,7,0.5)_56%,rgba(9,9,7,0.82)),linear-gradient(0deg,rgba(17,19,15,0.98),transparent_44%)]'
            : 'bg-[linear-gradient(90deg,rgba(9,9,7,0.92),rgba(9,9,7,0.5)_56%,rgba(9,9,7,0.82)),linear-gradient(0deg,rgba(17,19,15,0.98),transparent_44%)]'
        }`}
      />
      <motion.div
        aria-hidden="true"
        animate={{ y: [0, -16, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        className={`absolute top-16 -z-10 h-[44vw] w-[44vw] rounded-full border border-[#d7b56d]/18 ${
          align === 'right' ? 'left-[-22vw]' : 'right-[-22vw]'
        }`}
      />

      <div className={`flex min-h-[68svh] items-end ${align === 'right' ? 'justify-end text-right' : 'justify-start'}`}>
        <Reveal className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#d7b56d]">{role}</p>
          {(name || person.nickname) && (
            <h2 className="mt-4 text-[clamp(3rem,8vw,8rem)] font-semibold leading-[0.9] tracking-normal">
              {name || person.nickname}
            </h2>
          )}
          {person.profession && <p className="mt-5 text-lg text-[#f7eee0]/78 sm:text-xl">{person.profession}</p>}
          {person.about && (
            <p className="mt-7 max-w-2xl text-lg leading-8 text-[#f7eee0]/78 sm:text-2xl sm:leading-10">
              {person.about}
            </p>
          )}
          <div className={`mt-9 grid gap-4 sm:grid-cols-2 ${align === 'right' ? 'lg:ml-auto' : ''}`}>
            {parents && (
              <div className="min-w-0 border-t border-[#f7eee0]/22 pt-5">
                <UsersRound className={`mb-4 text-[#d7b56d] ${align === 'right' ? 'ml-auto' : ''}`} size={22} />
                <p className="text-xs uppercase tracking-[0.18em] text-[#f7eee0]/55">Family</p>
                <p className="mt-2 break-words text-base font-semibold leading-7">{parents}</p>
              </div>
            )}
            {person.homeAddress && (
              <div className="min-w-0 border-t border-[#f7eee0]/22 pt-5">
                <Home className={`mb-4 text-[#d7b56d] ${align === 'right' ? 'ml-auto' : ''}`} size={22} />
                <p className="text-xs uppercase tracking-[0.18em] text-[#f7eee0]/55">Home</p>
                <p className="mt-2 break-words text-base font-semibold leading-7">{person.homeAddress}</p>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function CoupleStorySection({ groom, bride, story }: Props) {
  const hasStory = Boolean(story.trim());
  const showGroom = hasPersonDetails(groom);
  const showBride = hasPersonDetails(bride);
  if (!hasStory && !showGroom && !showBride) return null;

  return (
    <>
      {hasStory && (
        <section className="relative overflow-hidden bg-[#f8f1e5] px-5 py-20 text-[#1f1b15] sm:px-8 lg:px-14 lg:py-28">
          <div className="absolute inset-y-0 right-0 w-2/5 bg-[#d8a1a0]/16" />
          <Reveal className="relative max-w-6xl">
            <Sparkles className="mb-6 text-[#a15f54]" size={30} />
            <p className="text-xs font-semibold uppercase tracking-[0.26em] text-[#a15f54]">Couple Story</p>
            <h2 className="mt-4 text-[clamp(2.35rem,5.6vw,5.8rem)] font-semibold leading-[0.98] tracking-normal">
              Two families, one unfolding story.
            </h2>
            <p className="mt-8 max-w-3xl text-lg leading-8 text-[#51463a] sm:text-2xl sm:leading-10">
              {story}
            </p>
          </Reveal>
        </section>
      )}
      {showGroom && <PersonStory person={groom} role="Groom" />}
      {showBride && <PersonStory person={bride} role="Bride" align="right" />}
    </>
  );
}
